import { type Locale, _ } from "i18n";
import ContinentContainer from "./ContinentContainer";
import { ContinentData, CountryData, CountrySelection, DataSourceOption, UserCountrySelection } from "./types";
import { useCallback, useEffect, useRef, useState } from "react";
import ContinentSelector from "./ContinentSelector";
import { DataSourceSelector } from "./DataSourceSelector";
import { GenderSelection } from "./GenderSelection";
import Modal from "../Modal";
import Loader from "../Loader/Loader";
import { Continent, Gender } from "./types";
import type { GenderOptionsInput } from "./GenderSelection";
import clsx from "clsx";
import {TeamGroupSelector} from "./TeamGroupSelector";
import { TeamGroupTeams, type TeamGroupVisibleEntities } from "./TeamGroupTeams";

interface CountrySelectionModalProps {
	countryData: CountryData;
	selection: UserCountrySelection["countries"];
	selectedGender?: Gender;
	selectedTeamGroups?: string[];
	selectedDataSource?: string | null;
	/** When `genderSelector` is true, the second argument is the chosen profile gender. When false (e.g. admin shell), only countries are passed. */
	onSave: (selection: CountrySelection, gender?: Gender, scope?: string) => void;
	onClose: () => void;
	teamGroupSelector?: boolean;
	genderSelector?: boolean;
	dataSourceSelector?: boolean;
	locale: Locale;
	/** When female is false, the Female control is disabled (see /me `genderOptions`). */
	genderOptions?: GenderOptionsInput;
	/** Called when the user switches Male/Female while the modal is open (portal country list preview). */
	onViewChange?: (gender: Gender, scope: string, teamGroups: string[]) => void;
	teamGroupOptions?: DataSourceOption[];
	dataSourceOptions?: DataSourceOption[];
	/** Base URL for resolving relative logo paths from the API (e.g. portal `apiURL`). */
	logoBasePath?: string;
	/** True while the all-countries list is being refetched (e.g. after gender change). */
	isCountriesLoading?: boolean;
	fetchVisibleTeamGroupEntities?: (params: {
		gender: Gender;
		teamGroups: string[];
		countries: string[];
	}) => Promise<TeamGroupVisibleEntities>;
}

function dedupeCountryCodes(codes: string[]): string[] {
	const seen = new Set<string>();
	const deduped: string[] = [];

	for (const code of codes) {
		if (seen.has(code)) {
			continue;
		}
		seen.add(code);
		deduped.push(code);
	}

	return deduped;
}

function dedupeContinentData(data: ContinentData | undefined): ContinentData | undefined {
	if (data === undefined) {
		return undefined;
	}

	const seen = new Set<string>();

	return data.filter((country) => {
		if (seen.has(country.code)) {
			return false;
		}
		seen.add(country.code);
		return true;
	});
}

function cloneCountrySelection(selection: CountrySelection): CountrySelection {
	const cloned = {} as CountrySelection;

	for (const continent of Object.keys(selection) as Continent[]) {
		cloned[continent] = dedupeCountryCodes(selection[continent] ?? []);
	}

	return cloned;
}

function getSelectedContinentsInitialValue(selectedCountries: CountrySelection): Continent[] {
	return Object.entries(selectedCountries)
		.map(([continent, selectedCountries]) => {
			if (selectedCountries.length > 0) {
				return continent;
			}
		})
		.filter(Boolean) as Continent[];
}

function sanitizeCountrySelection(selection: CountrySelection, countryData: CountryData): CountrySelection {
	const sanitized = {} as CountrySelection;

	for (const continent of Object.keys(selection) as Continent[]) {
		const enabledCodes = new Set(
			(countryData[continent] ?? []).filter((country) => country.enabled).map((country) => country.code),
		);
		sanitized[continent] = dedupeCountryCodes(
			(selection[continent] ?? []).filter((code) => enabledCodes.has(code)),
		);
	}

	return sanitized;
}

function hasSameCountrySelection(a: CountrySelection, b: CountrySelection): boolean {
	return (Object.keys(a) as Continent[]).every((continent) => {
		const aCodes = a[continent] ?? [];
		const bCodes = b[continent] ?? [];
		return aCodes.length === bCodes.length && aCodes.every((code, index) => code === bCodes[index]);
	});
}

export function CountrySelectionModal({
	countryData,
	selection,
	onSave,
	onClose,
	locale,
	genderSelector,
	teamGroupSelector,
	dataSourceSelector,
	selectedGender,
	selectedTeamGroups,
	selectedDataSource,
	genderOptions,
	teamGroupOptions,
	dataSourceOptions,
	onViewChange,
	isCountriesLoading = false,
	logoBasePath,
	fetchVisibleTeamGroupEntities,
}: CountrySelectionModalProps) {
	const [selectedContinents, setSelectedContinents] = useState<Continent[]>(() =>
		getSelectedContinentsInitialValue(cloneCountrySelection(selection)),
	);
	const [userSelectedCountries, setUserSelectedCountries] = useState(() => cloneCountrySelection(selection));
	const userSelectedCountriesRef = useRef(userSelectedCountries);
	userSelectedCountriesRef.current = userSelectedCountries;
	const [genderState, setGenderState] = useState<Gender>(() => {
		const initial = selectedGender ?? Gender.MALE;
		if (genderOptions && !genderOptions.female && initial === Gender.FEMALE) {
			return Gender.MALE;
		}
		return initial;
	});
	const [dataSourceState, setDataSourceState] = useState<string>(() => selectedDataSource ?? "scout");
	const [teamGroupsState, setTeamGroupsState] = useState<string[]>(selectedTeamGroups);
	const [teamGroupsEnabled, setTeamGroupsEnabled] = useState<boolean>(dataSourceState === "team_groups");

	const applyGender = useCallback(
		(gender: Gender) => {
			setGenderState(gender);
			onViewChange?.(gender, dataSourceState, teamGroupsState);
		},
		[dataSourceState, onViewChange]
	);

	const applyDataSource = useCallback(
		(scope: string) => {
			setDataSourceState(scope);
			setTeamGroupsEnabled(scope === "team_groups")
			onViewChange?.(genderState, scope, teamGroupsState);
		},
		[genderState, onViewChange]
	);

	const applyTeamGroup = useCallback(
		(teamGroups: string[]) => {
			setTeamGroupsState(teamGroups);
			onViewChange?.(genderState, dataSourceState, teamGroups);
		},
		[genderState, dataSourceState, teamGroupsState, onViewChange]
	);

	useEffect(() => {
		if (genderOptions && !genderOptions.female && genderState === Gender.FEMALE) {
			applyGender(Gender.MALE);
		}
	}, [genderOptions, genderState, applyGender]);

	useEffect(() => {
		const prev = userSelectedCountriesRef.current;
		const sanitized = sanitizeCountrySelection(prev, countryData);
		if (hasSameCountrySelection(prev, sanitized)) {
			return;
		}
		setUserSelectedCountries(sanitized);
		setSelectedContinents(getSelectedContinentsInitialValue(sanitized));
	}, [countryData]);

	const getEnabledCountries = (continent: Continent): string[] => {
		const continentData = countryData[continent];
		if (continentData === undefined) {
			return [];
		}

		const seen = new Set<string>();
		const countries: string[] = [];

		for (const country of continentData) {
			if (country.enabled && !seen.has(country.code)) {
				seen.add(country.code);
				countries.push(country.code);
			}
		}

		return countries;
	};

	const handleContinentSelect = (continent: Continent) => {
		let continents: Continent[] = [];
		if (!selectedContinents.includes(continent)) {
			continents = [...selectedContinents, continent];
		}

		setSelectedContinents(continents);
	};

	const handleContinentDeselect = (continent: Continent) => {
		let continents: Continent[] = [];
		if (selectedContinents.includes(continent)) {
			continents = selectedContinents.filter((value: string) => value !== continent);
		}

		handleDeselectAllCountries(continent);

		setSelectedContinents(continents);
	};

	const handleSelectCountries = (continent: Continent, countries: string[]) => {
		setUserSelectedCountries((current) => ({
			...current,
			[continent]: dedupeCountryCodes([...(current[continent] ?? []), ...countries]),
		}));
	};

	const handleDeselectCountries = (continent: Continent, countries: string[]) => {
		const countriesToRemove = new Set(countries);
		setUserSelectedCountries((current) => ({
			...current,
			[continent]: (current[continent] ?? []).filter((countryCode) => !countriesToRemove.has(countryCode)),
		}));
	};

	const handleSelectAllCountries = (continent: Continent) => {
		setUserSelectedCountries((current) => ({
			...current,
			[continent]: getEnabledCountries(continent),
		}));
	};

	const handleDeselectAllCountries = (continent: Continent) => {
		setUserSelectedCountries((current) => ({
			...current,
			[continent]: [],
		}));
	};

	const continents: Continent[] = (Object.keys(countryData).sort() as Continent[]).filter((c) => {
		return (countryData[c] || []).length > 0;
	});

	const selectionCount: number = (Object.keys(userSelectedCountries) as Continent[]).reduce(
		(acc: number, key: Continent): number => acc + userSelectedCountries[key].length,
		0
	);

	const handleGenderChange = (gender: Gender) => {
		applyGender(gender);
	};

	const handleDataSourceChange = (scope: string) => {
		applyDataSource(scope);
	};

	const handleTeamGroupToggle = (teamGroups: string[]) => {
		applyTeamGroup(teamGroups);
	};

	const handleToggleTeamGroupsEnabled = (enabled: boolean) => {
		if (enabled) {
			applyDataSource("team_groups");
		} else {
			applyDataSource(dataSourceOptions[0]?.key);
		}
	};

	const shouldHideCountriesAndGender =
		teamGroupSelector === true && dataSourceState === "team_groups" && (teamGroupsState?.length ?? 0) === 0;

	const shouldShowTeamGroupTeams = teamGroupSelector === true && dataSourceState === "team_groups";
	const isTeamGroupToggleDisabled = dataSourceOptions?.find((ds) => ds.key === "team_groups")?.isSelectable !== true;

	return (
		<Modal size="xl" show={true} onClose={onClose} title={_("country.selection.choose.country")} className="countrySelection-modal">
			<div
				className={clsx("countrySelection-containerNew", isCountriesLoading && "countrySelection-containerNew--loading")}
				aria-busy={isCountriesLoading}
			>
				{isCountriesLoading && (
					<div className="countrySelection-containerNew__loader">
						<Loader />
					</div>
				)}
				{teamGroupSelector && (
					<TeamGroupSelector
						enabled={teamGroupsEnabled}
						options={teamGroupOptions}
						selected={teamGroupsState}
						onToggle={handleToggleTeamGroupsEnabled}
						onSelect={handleTeamGroupToggle}
						logoBasePath={logoBasePath}
						disabled={isTeamGroupToggleDisabled}
					/>
				)}
				{dataSourceSelector && (
					<DataSourceSelector
						options={dataSourceOptions}
						selected={dataSourceState}
						onChange={handleDataSourceChange}
					/>
				)}
				{genderSelector && !shouldHideCountriesAndGender && (
					<GenderSelection
						value={genderState}
						onChange={handleGenderChange}
						genderOptions={genderOptions}
					/>
				)}
				{!shouldHideCountriesAndGender && <>
					<div className="continent-list">
						{continents.map((continent) => {
							return (
								<ContinentSelector
									key={continent}
									continent={continent}
									isSelected={selectedContinents.includes(continent)}
									onSelect={handleContinentSelect}
									onDeselect={handleContinentDeselect}
								/>
							);
						})}
					</div>
					{continents.map((continent) => {
						return (
							<ContinentContainer
								key={continent}
								continent={continent}
								isSelected={selectedContinents.includes(continent)}
								data={dedupeContinentData(countryData[continent])}
								selection={userSelectedCountries}
								onSelect={handleSelectCountries}
								onDeselect={handleDeselectCountries}
								onSelectAll={handleSelectAllCountries}
								onDeselectAll={handleDeselectAllCountries}
								locale={locale}
							/>
						);
					})}
				</>}
				{shouldShowTeamGroupTeams && fetchVisibleTeamGroupEntities !== undefined && (
					<TeamGroupTeams
						selected={teamGroupsState}
						selectedCountries={userSelectedCountries}
						selectedGender={genderState}
						logoBasePath={logoBasePath}
						fetchVisibleEntities={fetchVisibleTeamGroupEntities}
					/>
				)}
			</div>
			<div className="modal__footer">
				<button
					type="button"
					className="footer-action btn"
					disabled={(selectionCount === 0 && !dataSourceState?.startsWith("team_group:")) || isCountriesLoading}
					onClick={() => {
						if (genderSelector || dataSourceSelector || teamGroupSelector) {
							onSave(userSelectedCountries, genderState, dataSourceState, teamGroupsState);
						} else {
							onSave(userSelectedCountries);
						}
					}}
				>
					{_("country.selection.save.btn")} {selectionCount > 0 && "(" + selectionCount + ")"}
				</button>
			</div>
		</Modal>
	);
}
